import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { getTopicIndex } from "@/lib/topics.functions";
import type { TopicEntry } from "@/lib/topics.server";
import { breadcrumbLd, faqLd, SITE_OG_IMAGE, pageTitle, toolPageLd } from "@/lib/seo";
import { FaqBlock } from "@/components/FaqBlock";

const PATH = "https://autospecdata.com/how-much-can-my-car-tow";
const TITLE = "How Much Can My Car Tow? Tow Rating Lookup";
const ANSWER =
  "Find your vehicle below to get its manufacturer-published maximum tow rating, then plan around 80% of that figure. The published maximum assumes a properly equipped vehicle with a 150 lb driver and nothing else on board — every passenger, every pound of cargo and the trailer's tongue weight comes out of it.";

const FAQS = [
  { q: "How much can my car tow?", a: ANSWER },
  {
    q: "Why use 80% of the rated capacity?",
    a: "The published maximum is a peak figure measured on a properly equipped vehicle at sea level with one occupant. Loaded with passengers, gear and tongue weight, and on a grade in summer heat, transmission and coolant temperatures climb fast at the limit. 80% is the working figure most towing guides and RV dealers use for repeated, loaded towing.",
  },
  {
    q: "Does tongue weight count against payload or towing?",
    a: "Payload. Tongue weight is 10–15% of trailer weight for a conventional hitch and 15–25% for a fifth wheel, and it sits on the vehicle's axles — so a 7,000 lb trailer puts roughly 700–1,050 lb into the bed or hitch before you load a single passenger. Payload, not tow rating, is what most trucks run out of first.",
  },
  {
    q: "Is the tow rating the same for every trim?",
    a: "No. Cab style, bed length, axle ratio, engine and the presence of a factory tow package all move the number, often by thousands of pounds within one model year. The figure shown here is the highest published rating for that model; the model page lists the configuration each rating applies to.",
  },
  {
    q: "Can I tow more with a weight-distributing hitch?",
    a: "Sometimes — many manufacturers publish a lower rating for a dead-weight hitch and a higher one with weight distribution, commonly capping dead-weight towing around 5,000 lb. A weight-distributing hitch does not raise the vehicle's maximum; it lets you reach it safely by restoring front-axle load.",
  },
];

export const Route = createFileRoute("/how-much-can-my-car-tow")({
  loader: () => getTopicIndex({ data: { slug: "towing-capacity" } }),
  component: TowLookup,
  head: () => ({
    meta: [
      { title: pageTitle(TITLE) },
      { name: "description", content: ANSWER.slice(0, 158) },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: ANSWER.slice(0, 158) },
      { property: "og:url", content: PATH },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: PATH }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          toolPageLd({ name: TITLE, path: PATH, description: ANSWER }),
        ),
      },

      { type: "application/ld+json", children: JSON.stringify(faqLd(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "How Much Can My Car Tow", path: PATH },
          ]),
        ),
      },
    ],
  }),
});

const lbs = (n: number) => Math.round(n).toLocaleString("en-US");

function TowLookup() {
  const data = Route.useLoaderData();
  const entries = data?.entries ?? [];

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<TopicEntry | null>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return entries
      .filter((e) => `${e.makeName} ${e.modelName}`.toLowerCase().includes(q))
      .slice(0, 12);
  }, [query, entries]);

  const safe = selected ? selected.value * 0.8 : 0;
  const tongueLow = selected ? selected.value * 0.1 : 0;
  const tongueHigh = selected ? selected.value * 0.15 : 0;

  return (
    <div>
      <nav className="mx-auto max-w-4xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">How Much Can My Car Tow</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            How Much Can My Car Tow?
          </h1>
          <p
            data-quick-answer
            data-aeo="atomic-passage"
            className="mt-4 text-lg text-muted-foreground"
          >
            {ANSWER}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-foreground">
            Search your make and model
          </span>
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
            }}
            placeholder="e.g. Toyota Tacoma"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground outline-none focus:border-primary"
          />
        </label>

        {matches.length > 0 && !selected ? (
          <ul className="mt-3 divide-y divide-border/60 rounded-md border border-border">
            {matches.map((e) => (
              <li key={`${e.makeSlug}-${e.modelSlug}-${e.year}`}>
                <button
                  type="button"
                  onClick={() => setSelected(e)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-secondary/40"
                >
                  <span className="text-foreground">
                    {e.makeName} {e.modelName}
                  </span>
                  <span className="text-sm text-muted-foreground">{lbs(e.value)} lbs max</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {query.trim().length >= 2 && matches.length === 0 && !selected ? (
          <p className="mt-3 text-sm text-muted-foreground">
            No published tow rating on file for that search. Most cars and crossovers without a
            factory tow package have no rating at all in the United States, which means zero, not
            "a little".
          </p>
        ) : null}

        {selected ? (
          <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-5">
            <h2 className="font-heading text-xl font-bold text-foreground">
              {selected.makeName} {selected.modelName}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Highest published rating, {selected.year} model year
              {selected.config ? ` — ${selected.config}` : ""}.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  Maximum rating
                </span>
                <span className="mt-1 block font-heading text-2xl font-bold text-primary">
                  {lbs(selected.value)} lbs
                </span>
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  80% working load
                </span>
                <span className="mt-1 block font-heading text-2xl font-bold text-foreground">
                  {lbs(safe)} lbs
                </span>
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  Tongue weight at max
                </span>
                <span className="mt-1 block font-heading text-2xl font-bold text-foreground">
                  {lbs(tongueLow)}–{lbs(tongueHigh)} lbs
                </span>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              That tongue weight lands on your payload, not your tow rating. Subtract it, plus
              passengers and cargo, before comparing the {lbs(safe)} lb working figure to a
              trailer's loaded weight — not its dry weight, which excludes fluids and gear.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link
                to="/$make/$model/$dataType"
                params={{
                  make: selected.makeSlug,
                  model: selected.modelSlug,
                  dataType: "towing-capacity",
                }}
                className="cta-gold"
              >
                Year-by-year ratings for this model
              </Link>
              <Link to="/tools/$slug" params={{ slug: "payload-calculator" }} className="text-primary hover:underline">
                Payload calculator
              </Link>
            </div>
          </div>
        ) : null}
      </section>

      <section className="border-t border-border bg-secondary/20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
          <h2 className="text-2xl font-bold text-foreground">Read the number correctly</h2>
          <ul className="mt-4 space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Max tow is a single configuration.</strong> It is
              usually the lightest cab, the longest bed, the optional axle ratio and the tow
              package. Change any one and the rating drops, often by 1,500–3,000 lb.
            </li>
            <li>
              <strong className="text-foreground">Trailer dry weight is not trailer weight.</strong>{" "}
              Add fluids, batteries, propane and cargo — 800 to 1,500 lb on a typical travel
              trailer.
            </li>
            <li>
              <strong className="text-foreground">Payload runs out before tow rating.</strong> A
              half-ton truck rated at 11,000 lb frequently has 1,600 lb of payload, and a 10,000 lb
              trailer puts 1,000–1,500 lb of that on the hitch before anyone gets in.
            </li>
            <li>
              <strong className="text-foreground">Altitude derates the engine.</strong> Naturally
              aspirated engines lose roughly 3% of power per 1,000 ft. Turbocharged and diesel
              engines lose far less, which is why they dominate mountain towing.
            </li>
          </ul>
          <p className="mt-6 text-sm text-muted-foreground">
            Browse every rating on the{" "}
            <Link to="/towing-capacity" className="text-primary hover:underline">
              towing capacity chart
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
          <FaqBlock items={FAQS} />
        </div>
      </section>
    </div>
  );
}
